(ns graph-ui.web.locate
  "Client channel for the live-preview 'Select' mode → the Source (EDN) editor —
   PLAN-graph-ui-v0-006-01, the Studio Designer's click-to-source link-select.

   When the user turns on Select in the :island-view preview and clicks a widget,
   island-view publishes that widget-id here under a key of [session-id source];
   the Source :text-area (bound to the same source) derefs the same key, finds the
   node's {…} span in its OWN buffer, and selects it. Purely client-side and the
   same shape as graph-ui.web.draft — it never touches the bus (the mirror
   selection into the Widget-tree/Selected-widget panels is a separate
   :ui/select-widget dispatch island-view makes).

   The brace scan is string/escape aware so a :value \"with a } brace\" can never
   fool it, and it is a pure function (node-span) so it is unit-tested headlessly."
  (:require [reagent.core :as r]
            [clojure.string :as str]))

;; @feature F-graph-ui-v0-005-01-studio-designer-link-select
;; @adr ADR-ikc-v3-050-01-studio-self-hosting

(defonce ^:private targets (r/atom {}))
(defn- k [session-id source] [(str session-id) source])

(defn request!
  ;; CONCEPT: CONCEPT-graph-ui-v0-001-01-three-graphs
  "Publish a locate request: select the node carrying `widget-id` in the Source
   editor bound to [session-id source]. The :nonce makes re-clicking the SAME
   widget fire again (the reader re-selects), so a stray cursor move is always
   recoverable by clicking once more."
  [session-id source widget-id]
  (swap! targets assoc (k session-id source)
         {:widget-id (str widget-id) :nonce (js/Date.now)}))

(defn target
  ;; CONCEPT: CONCEPT-graph-ui-v0-001-01-three-graphs
  "Reactive read of the pending locate request for [session-id source], or nil.
   Deref happens inside @targets, so a reagent render calling this re-runs when a
   new request is published."
  [session-id source]
  (get @targets (k session-id source)))

(defn- enclosing-open
  "Index of the innermost '{' still open at position `upto`, scanning [0,upto),
   ignoring braces inside \"strings\" and after a \\ escape. nil if none open."
  [text upto]
  (let [n (min upto (count text))]
    (loop [i 0 in-str false esc false stack ()]
      (if (>= i n)
        (first stack)
        (let [c (nth text i)]
          (cond
            esc                     (recur (inc i) in-str false stack)
            (and in-str (= c "\\")) (recur (inc i) in-str true stack)
            (= c "\"")              (recur (inc i) (not in-str) false stack)
            in-str                  (recur (inc i) in-str false stack)
            (= c "{")               (recur (inc i) in-str false (conj stack i))
            (= c "}")               (recur (inc i) in-str false (rest stack))
            :else                   (recur (inc i) in-str false stack)))))))

(defn- matching-close
  "Index of the '}' closing the '{' at `open`, string/escape aware. nil if the
   braces don't balance before end-of-text."
  [text open]
  (let [n (count text)]
    (loop [i open in-str false esc false depth 0]
      (if (>= i n)
        nil
        (let [c (nth text i)]
          (cond
            esc                     (recur (inc i) in-str false depth)
            (and in-str (= c "\\")) (recur (inc i) in-str true depth)
            (= c "\"")              (recur (inc i) (not in-str) false depth)
            in-str                  (recur (inc i) in-str false depth)
            (= c "{")               (recur (inc i) in-str false (inc depth))
            (= c "}")               (if (= depth 1) i (recur (inc i) in-str false (dec depth)))
            :else                   (recur (inc i) in-str false depth)))))))

(defn node-span
  ;; CONCEPT: CONCEPT-graph-ui-v0-001-01-three-graphs
  "[start end) of the smallest EDN map `{…}` in `text` that contains
   `:widget-id \"<widget-id>\"`, or nil when that id is not in the text. `end` is
   exclusive (the char after the closing brace), suitable for setSelectionRange."
  [text widget-id]
  (let [needle (str ":widget-id \"" widget-id "\"")
        i      (str/index-of text needle)]
    (when i
      (let [open  (enclosing-open text i)
            close (when open (matching-close text open))]
        (when (and open close) [open (inc close)])))))
